import * as React from "react";
import styled from "@mui/material/styles/styled";
import ListItem from "@mui/material/ListItem";
import { ListItemSecondaryAction, Theme } from "@mui/material";
import { CONDENSED_FONT_LT } from "fe-web-components/styles/typography";
import { QualifiedFeature } from "fe-common/graphql";
import ListItemText from "@mui/material/ListItemText/ListItemText";
import { getQFAndRefinementsLines } from "fe-common/logic";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { DesktopOnly } from "../util/desktopOnly";
import { MobileOnly } from "../util/mobileOnly";
import { ActionButton } from "fe-common/ui/buttons/actionButton";
import FilterListIcon from "@mui/icons-material/FilterList";
import IconButton from "@mui/material/IconButton";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { AgentTooltip } from "fe-common/ui/AgentTooltip";
import RefineIconSVG from "../../../assets/refineIcon.svg";
import { findingFormat } from "fe-web-components/formatting/FindingNameFormatter";
import { MouseEventHandler } from "react";

//region [[ Styles ]]

const FindingItemText = styled(ListItemText)(({ theme }) => ({
  "margin-bottom": "6px",
}));

const FindingContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  [theme.breakpoints.down(MobileView)]: {
    flexDirection: "row-reverse",
    justifyContent: "space-around",
    flex: 1,
  },
}));

const FindingItemTypography = styled("span")(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 400,
  color: "black",
  fontFamily: CONDENSED_FONT_LT,

  [theme.breakpoints.down(MobileView)]: {
    fontSize: "14px",
    fontWeight: 500,
    maxWidth: "200px",
    color: KahunColorMap.PrimaryDim,
  },
}));

const FindingRefinementsItemTypography = styled("span")(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 400,
  color: RosebudColorMap.gray1,
  fontFamily: CONDENSED_FONT_LT,
}));

const RefineIcon = styled(FilterListIcon)({
  color: KahunColorMap.PrimaryDim,
});

const RefineIconContainer = styled(IconButton)({
  cursor: "pointer",
});

const DeleteIconContainer = styled(IconButton)({
  marginRight: "4px",
  paddingLeft: "0px",
});

const DeleteIcon = styled(HighlightOffIcon)(({ theme }) => ({
  color: KahunColorMap.PrimaryDim,
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "20px",
  },
}));

const MainOutlinedButton = styled(ActionButton)({
  borderRadius: "5px",
  padding: "2px",
  height: "22px",
  fontSize: "12px",
});

const RefinementContainer = styled("span")({});

const LeftSide = styled("div")({
  display: "flex",
  alignItems: "center",
});

const FindingActions = styled(ListItemSecondaryAction)(({ theme }) => ({
  display: "flex",
  top: "24px",

  [theme.breakpoints.down(MobileView)]: {
    right: 0,
    top: "22px",

    "& .action-button": {
      borderRadius: "5px",
      padding: "2px",
      height: "22px",
      fontSize: "12px",
    },

    "& .refine-button": {
      borderRadius: "5px",
      padding: "2px",
      height: "22px",
      marginTop: "2px",
      fontSize: "12px",
      borderColor: KahunColorMap.PrimaryDim,
      color: KahunColorMap.PrimaryDim,
    },
  },
}));

const FindingListItem = styled(ListItem)(
  (props: {
    indented: "true" | "false";
    clickable: "true" | "false";
    theme?: Theme;
    onClick: (e: any) => void;
  }) => ({
    alignItems: "center",
    paddingRight: "60px",
    paddingLeft: props.indented === "true" ? "30px" : "16px",
    cursor: props.clickable ? "pointer" : "unset",
    pointerEvents: props.clickable ? "unset" : "none",
    [props.theme.breakpoints.down(MobileView)]: {
      borderBottom: "1px solid " + KahunColorMap.Divider,
      minHeight: "45px",
      paddingLeft: "0px",
      paddingRight: "25%",
    },
  }),
);
//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientChartFindingProps {
  qf: QualifiedFeature;
  gotoSection: (subGroupId: any) => void;
  index: number;
  onDelete?: (event) => void;
  isEditable: boolean;
  indented?: boolean;
  onRefine?: (e: any) => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientChartFinding = ({ ...props }: PatientChartFindingProps) => {
  const [findingTitle, findingQualifiersText] = getQFAndRefinementsLines(
    props.qf,
  );

  // @todo - bring back when working on feature of change yes no popper
  //const change = extractResponseFromQF(props.qf);

  return (
    <FindingListItem
      indented={props.indented ? "true" : "false"}
      key={props.qf?.id}
      onClick={props.onRefine}
      clickable={"true"}
      dense
      disableGutters
    >
      <FindingContainer>
        {/* @todo - bring back when working on feature of change yes no popper*/}
        {/*<YesNoIndicator negate={change.negate} level={change.level} />*/}
        {/* todo - cancel No from finding title in a normal way" */}
        <FindingItemText
          key={findingTitle}
          primary={
            <LeftSide>
              {props.onDelete && (
                <MobileOnly>
                  <DeleteIconContainer size={"small"} onClick={props.onDelete}>
                    <AgentTooltip title={"Delete"}>
                      <DeleteIcon fontSize={"small"} />
                    </AgentTooltip>
                  </DeleteIconContainer>
                </MobileOnly>
              )}
              <FindingItemTypography>
                {findingFormat(props.qf)}
              </FindingItemTypography>
            </LeftSide>
          }
          secondary={
            <RefinementContainer>
              {findingQualifiersText && (
                <FindingRefinementsItemTypography>
                  {findingQualifiersText}
                </FindingRefinementsItemTypography>
              )}
            </RefinementContainer>
          }
        />
      </FindingContainer>
      <FindingActions>
        {props.onRefine && (
          <>
            <DesktopOnly>
              <RefineIconContainer size={"small"} onClick={props.onRefine}>
                <AgentTooltip title={"Refine"}>
                  <RefineIcon fontSize={"small"} />
                </AgentTooltip>
              </RefineIconContainer>
            </DesktopOnly>
            <MobileOnly>
              <MainOutlinedButton
                icon={<img src={RefineIconSVG} alt={"Refine"} />}
                label={"Refine"}
                onClick={props.onRefine}
                variant={"outlined"}
                className={"refine-button"}
              />
            </MobileOnly>
          </>
        )}

        {props.onDelete && (
          <DesktopOnly>
            <RefineIconContainer size={"small"} onClick={props.onDelete}>
              <AgentTooltip title={"Delete"}>
                <DeleteIcon fontSize={"small"} />
              </AgentTooltip>
            </RefineIconContainer>
          </DesktopOnly>
        )}
      </FindingActions>
    </FindingListItem>
  );
};
